import { useState, useEffect } from 'react'
import api from '../services/api'
import SortableTh, { sortRows } from '../components/SortableTh'
import Paginacao, { usePaginacao } from '../components/Paginacao'
import Apolices from './Apolices'
import IconVisualizar from '../components/icons/IconVisualizar'
import IconDarBaixaComissao from '../components/icons/IconDarBaixaComissao'
import { avisar, confirmar } from '../components/Avisos'
import SelecaoMultipla from '../components/SelecaoMultipla'

// DATE/strings de data vem sempre como "AAAA-MM-DD" simples (parcelas_comissao e JSONB, nao
// coluna DATE do Postgres) — sem risco do bug de fuso horario que existia em Financeiro/Sinistros/
// Assistencias, mas mantemos a mesma forma de ancorar em meia-noite local por consistencia.
const fmtData = (d) => d ? new Date(d.split('T')[0] + 'T00:00:00').toLocaleDateString('pt-BR') : '—'

// Valor das parcelas de comissao vem formatado ("R$ x.xxx,xx", string) de dentro de
// apolices.parcelas_comissao — mesmo parser/formatter usado nos campos moeda de Apolices.jsx.
const parseMoneyInput = (v) => {
  const s = (v || '').toString()
  const negativo = s.includes('-')
  const n = parseFloat(s.replace(/[^0-9,]/g, '').replace(',', '.')) || 0
  return negativo ? -n : n
}
const formatMoneyInput = (v) => {
  const n = parseFloat(v) || 0
  const sinal = n < 0 ? '-' : ''
  return sinal + 'R$ ' + Math.abs(n).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

const statusInfo = (s) => ({
  Pendente: { label: 'Pendente', cls: 'badge-amber' },
  Recebido: { label: 'Recebido', cls: 'badge-green' },
  Atrasado: { label: 'Atrasado', cls: 'badge-red' },
}[s] || { label: s || '—', cls: 'badge-gray' })

// "Atrasado" e sempre calculado, nunca lido do banco: uma parcela ainda Pendente cujo vencimento
// ja passou vira Atrasado automaticamente, sem precisar abrir a apolice e mudar o status na mao.
// Recebido continua vindo do dado salvo (isso e um fato, nao algo pra calcular).
const hojeStr = () => new Date().toISOString().split('T')[0]
const statusEfetivo = (p) => {
  if (p.status === 'Recebido') return 'Recebido'
  if (p.data_vencimento && p.data_vencimento.split('T')[0] < hojeStr()) return 'Atrasado'
  return 'Pendente'
}

const colunasComissoes = [
  { key: 'cliente_nome',    label: 'Cliente',        get: p => p.cliente_nome || '' },
  { key: 'apolice',         label: 'Apólice',        get: p => p.numero_apolice || p.emissao_numero_apolice || '' },
  { key: 'seguradora',      label: 'Seguradora',     get: p => p.seguradora || '' },
  { key: 'numero',          label: 'Parcela',        get: p => p.numero || 0, numeric: true },
  { key: 'data_vencimento', label: 'Vencimento',     get: p => p.data_vencimento || '', date: true },
  { key: 'valor_previsto',  label: 'Valor Previsto', get: p => parseMoneyInput(p.valor_previsto), numeric: true },
  { key: 'status',          label: 'Status',         get: p => statusInfo(statusEfetivo(p)).label },
  { key: 'data_recebimento', label: 'Recebimento',   get: p => p.data_recebimento || '', date: true },
  { key: 'valor_recebido',  label: 'Valor Recebido', get: p => p.valor_recebido != null ? parseMoneyInput(p.valor_recebido) : 0, numeric: true },
]

// ===== Modal de baixa (marcar parcela como recebida, com valor/data efetivos — podem diferir do
// previsto) =====
function BaixaComissaoModal({ parcela, onClose, onSalvo }) {
  const [valorRecebido, setValorRecebido] = useState(parcela.valor_recebido ? formatMoneyInput(parseMoneyInput(parcela.valor_recebido)) : formatMoneyInput(parseMoneyInput(parcela.valor_previsto)))
  const [dataRecebimento, setDataRecebimento] = useState(parcela.data_recebimento || new Date().toISOString().split('T')[0])
  const [salvando, setSalvando] = useState(false)

  const salvar = async () => {
    if (!dataRecebimento) { avisar('Informe a data de recebimento.'); return }
    setSalvando(true)
    try {
      await api.patch(`/comissoes/${parcela.apolice_id}/${parcela.numero}`, {
        status: 'Recebido', valor_recebido: valorRecebido, data_recebimento: dataRecebimento,
      })
      onSalvo()
      onClose()
    } catch (err) {
      avisar('Erro ao dar baixa: ' + (err.response?.data?.erro || err.message))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <span className="modal-title">Dar Baixa na Comissão</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
          {parcela.cliente_nome} · {parcela.seguradora} · Parcela {parcela.numero} · Previsto {formatMoneyInput(parseMoneyInput(parcela.valor_previsto))}
        </div>
        <div className="form-group">
          <label className="form-label">Valor Recebido *</label>
          <input className="form-input" value={valorRecebido} onChange={e => setValorRecebido(e.target.value)}
            onBlur={e => setValorRecebido(formatMoneyInput(parseMoneyInput(e.target.value)))} placeholder="R$ 0,00" />
        </div>
        <div className="form-group">
          <label className="form-label">Data de Recebimento *</label>
          <input className="form-input" type="date" value={dataRecebimento} onChange={e => setDataRecebimento(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Confirmar Baixa'}</button>
        </div>
      </div>
    </div>
  )
}

// ===== Importar extrato de comissão (PDF/XLS/XLSX/CSV) — extrai os itens no backend
// (utils/extratoComissao.js, um parser por seguradora) e casa cada um com uma parcela da lista já
// carregada aqui na tela; usuário confere/edita valor e data antes de confirmar em lote. =====
const soDigitos = (v) => (v || '').toString().replace(/\D/g, '')
const encontrarParcela = (extraido, parcelas) => {
  const alvo = soDigitos(extraido.numero_apolice)
  if (!alvo) return null
  const candidatas = parcelas.filter(p => {
    const d = soDigitos(p.numero_apolice || p.emissao_numero_apolice)
    return d && (d === alvo || d.endsWith(alvo) || alvo.endsWith(d))
  })
  if (candidatas.length === 0) return null
  if (extraido.numero_parcela) {
    const exata = candidatas.find(p => p.numero === extraido.numero_parcela)
    if (exata) return exata
  }
  const pendentes = candidatas.filter(p => p.status !== 'Recebido').sort((a, b) => a.numero - b.numero)
  return pendentes[0] || candidatas[0]
}

function ImportarExtratoModal({ parcelas, onClose, onConfirmado, onVerApolice }) {
  const [itens, setItens] = useState([])
  const [enviando, setEnviando] = useState(false)
  const [confirmando, setConfirmando] = useState(false)
  const [erroArquivo, setErroArquivo] = useState('')

  // Se o usuario for ajustar algo na apolice (botao "Ver") e voltar, os itens ja importados
  // continuam aqui — so re-casa cada um com a lista de parcelas atualizada, sem perder o que ja foi
  // conferido/editado (valor recebido, data, selecao).
  useEffect(() => {
    setItens(prev => prev.map(it => ({ ...it, parcela: encontrarParcela(it.extraido, parcelas) })))
  }, [parcelas])

  const selecionarArquivo = async (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    setErroArquivo('')
    setEnviando(true)
    const erros = []
    try {
      // Um arquivo de cada vez (sequencial) — sao poucos arquivos por vez na pratica, e assim o
      // erro de um extrato nao impede os outros de serem lidos.
      for (const file of files) {
        try {
          const formData = new FormData()
          formData.append('arquivo', file)
          const { data } = await api.post('/comissoes/importar-extrato', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
          if (!data.itens || data.itens.length === 0) {
            erros.push(`${file.name}: seguradora identificada (${data.seguradora}), mas nenhum item foi encontrado.`)
            continue
          }
          const novos = data.itens.map((extraido, i) => {
            const parcela = encontrarParcela(extraido, parcelas)
            return {
              id: `${Date.now()}-${file.name}-${i}`,
              extraido,
              parcela,
              selecionado: !!parcela && parcela.status !== 'Recebido',
              valorRecebido: formatMoneyInput(extraido.valor_pago),
              dataRecebimento: extraido.data_pagamento || new Date().toISOString().split('T')[0],
            }
          })
          setItens(prev => [...prev, ...novos])
        } catch (err) {
          erros.push(`${file.name}: ${err.response?.data?.erro || err.message}`)
        }
      }
      if (erros.length > 0) setErroArquivo(erros.join(' \n'))
    } finally {
      setEnviando(false)
      e.target.value = ''
    }
  }

  const toggleSelecionado = (id) => setItens(prev => prev.map(it => it.id === id ? { ...it, selecionado: !it.selecionado } : it))
  const editarCampo = (id, campo, valor) => setItens(prev => prev.map(it => it.id === id ? { ...it, [campo]: valor } : it))
  const removerItem = (id) => setItens(prev => prev.filter(it => it.id !== id))

  const confirmar = async () => {
    const selecionados = itens.filter(it => it.selecionado && it.parcela)
    if (selecionados.length === 0) { avisar('Selecione ao menos um item encontrado.'); return }
    setConfirmando(true)
    try {
      for (const it of selecionados) {
        await api.patch(`/comissoes/${it.parcela.apolice_id}/${it.parcela.numero}`, {
          status: 'Recebido', valor_recebido: it.valorRecebido, data_recebimento: it.dataRecebimento,
        })
      }
      onConfirmado()
      onClose()
    } catch (err) {
      avisar('Erro ao confirmar baixas: ' + (err.response?.data?.erro || err.message))
    } finally {
      setConfirmando(false)
    }
  }

  const totalSelecionados = itens.filter(it => it.selecionado && it.parcela).length

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 980 }}>
        <div className="modal-header">
          <span className="modal-title">Importar Extrato de Comissão</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
          Aceita PDF, XLS, XLSX ou CSV — a seguradora é identificada automaticamente. Pode selecionar vários arquivos de uma vez (de seguradoras diferentes inclusive); os itens vão se somando na lista abaixo. Confira/ajuste valor e data antes de confirmar.
        </div>
        <div style={{ marginBottom: 16 }}>
          <input type="file" accept=".pdf,.xls,.xlsx,.csv" multiple onChange={selecionarArquivo} disabled={enviando} />
          {enviando && <span style={{ fontSize: 12, color: '#888', marginLeft: 10 }}>Lendo arquivo...</span>}
          {erroArquivo && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 6, whiteSpace: 'pre-line' }}>{erroArquivo}</div>}
        </div>

        {itens.length > 0 && (
          <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th></th>
                  <th style={{ textAlign: 'left' }}>Segurado (extraído)</th>
                  <th style={{ textAlign: 'left' }}>Apólice</th>
                  <th>Parc.</th>
                  <th>Valor Previsto</th>
                  <th>Valor Recebido</th>
                  <th>Data Recebimento</th>
                  <th style={{ textAlign: 'left' }}>Encontrado no sistema</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {itens.map(it => {
                  const previsto = it.parcela ? parseMoneyInput(it.parcela.valor_previsto) : null
                  const divergente = previsto != null && Math.abs(parseMoneyInput(it.valorRecebido) - previsto) > 0.01
                  return (
                  <tr key={it.id} style={{ borderBottom: '1px solid #f0f0f0', opacity: it.parcela ? 1 : 0.6 }}>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={it.selecionado} disabled={!it.parcela} onChange={() => toggleSelecionado(it.id)} />
                    </td>
                    <td>{it.extraido.nome_segurado || '—'}</td>
                    <td>{it.extraido.numero_apolice || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{it.extraido.numero_parcela || '—'}</td>
                    <td style={{ textAlign: 'center', color: '#888' }} title="Valor cadastrado na apólice pra essa parcela">
                      {previsto != null ? formatMoneyInput(previsto) : '—'}
                    </td>
                    <td>
                      <input className="form-input" value={it.valorRecebido}
                        style={{ padding: '4px 8px', fontSize: 12, width: 100, background: divergente ? 'var(--primary-light)' : undefined, borderColor: divergente ? 'var(--primary)' : undefined }}
                        title={divergente ? 'Valor diferente do previsto na apólice' : undefined}
                        onChange={e => editarCampo(it.id, 'valorRecebido', e.target.value)}
                        onBlur={e => editarCampo(it.id, 'valorRecebido', formatMoneyInput(parseMoneyInput(e.target.value)))} />
                    </td>
                    <td>
                      <input className="form-input" style={{ padding: '4px 8px', fontSize: 12 }} type="date" value={it.dataRecebimento}
                        onChange={e => editarCampo(it.id, 'dataRecebimento', e.target.value)} />
                    </td>
                    <td>
                      {it.parcela
                        ? <span style={{ color: '#3B6D11' }}>{it.parcela.cliente_nome} · Parc. {it.parcela.numero}{it.parcela.status === 'Recebido' ? ' (já recebida)' : ''}</span>
                        : <span style={{ color: '#A32D2D' }}>Não encontrado</span>}
                    </td>
                    <td>
                      {it.parcela && (
                        <button type="button" className="btn btn-secondary btn-sm btn-icon" title="Ver" style={{ color: 'var(--primary)' }} onClick={() => onVerApolice(it.parcela.apolice_id)}><IconVisualizar size={14} /></button>
                      )}
                    </td>
                    <td><button type="button" onClick={() => removerItem(it.id)} style={{ background: 'none', border: 'none', color: '#bbb', cursor: 'pointer' }}>×</button></td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button type="button" className="btn btn-primary" onClick={confirmar} disabled={confirmando || totalSelecionados === 0}>
            {confirmando ? 'Confirmando...' : `Confirmar ${totalSelecionados} Baixa(s)`}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Comissoes() {
  const [parcelas, setParcelas] = useState([])
  const [carregando, setCarregando] = useState(true)
  const [parcelaBaixa, setParcelaBaixa] = useState(null)
  const [modalImportar, setModalImportar] = useState(false)
  const [documentoApolice, setDocumentoApolice] = useState(null)

  // "Ver" na tela de importacao: busca a apolice completa (a lista de comissoes so tem os campos
  // resumidos) e abre o formulario dela embutido, por cima do modal de importacao, sem navegar pra
  // outra pagina — ao fechar/salvar volta exatamente pra onde estava, com a lista de itens intacta.
  const abrirApolice = async (apoliceId) => {
    try {
      const { data } = await api.get(`/apolices/${apoliceId}`)
      if (!data || !data.id) { avisar('Não foi possível carregar os dados da apólice. Tente novamente.'); return }
      setDocumentoApolice(data)
    } catch (err) {
      avisar('Erro ao abrir a apólice: ' + (err.response?.data?.erro || err.message))
    }
  }
  // Status e Seguradora aceitam varias opcoes ao mesmo tempo (nenhuma = todas) — pedido do usuario, 26/10
  const [filtroStatus, setFiltroStatus] = useState([])
  const [filtroSeguradora, setFiltroSeguradora] = useState([])
  const [filtroVencimentoDe, setFiltroVencimentoDe] = useState('')
  const [filtroVencimentoAte, setFiltroVencimentoAte] = useState('')
  const [sortCol, setSortCol] = useState('data_vencimento')
  const [sortDir, setSortDir] = useState('asc')
  const toggleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('asc') }
  }

  const carregar = () => {
    setCarregando(true)
    api.get('/comissoes').then(r => setParcelas(Array.isArray(r.data) ? r.data : [])).catch(() => setParcelas([])).finally(() => setCarregando(false))
  }
  useEffect(() => { carregar() }, [])

  const desfazerBaixa = async (p) => {
    if (!(await confirmar('Desfazer a baixa desta parcela? Ela volta pra Pendente.'))) return
    await api.patch(`/comissoes/${p.apolice_id}/${p.numero}`, { status: 'Pendente', valor_recebido: null, data_recebimento: null })
    carregar()
  }

  const limparFiltros = () => { setFiltroStatus([]); setFiltroSeguradora([]); setFiltroVencimentoDe(''); setFiltroVencimentoAte('') }

  const seguradoras = [...new Set(parcelas.map(p => p.seguradora).filter(Boolean))].sort()

  const filtradas = parcelas.filter(p => {
    if (filtroStatus.length && !filtroStatus.includes(statusEfetivo(p))) return false
    if (filtroSeguradora.length && !filtroSeguradora.includes(p.seguradora)) return false
    if (filtroVencimentoDe && (!p.data_vencimento || p.data_vencimento < filtroVencimentoDe)) return false
    if (filtroVencimentoAte && (!p.data_vencimento || p.data_vencimento > filtroVencimentoAte)) return false
    return true
  })
  const filtradasOrdenadas = sortRows(filtradas, colunasComissoes, sortCol, sortDir)
  const paginacao = usePaginacao(filtradasOrdenadas)

  const aReceber = filtradas.filter(p => statusEfetivo(p) !== 'Recebido').reduce((s, p) => s + parseMoneyInput(p.valor_previsto), 0)
  const atrasado = filtradas.filter(p => statusEfetivo(p) === 'Atrasado').reduce((s, p) => s + parseMoneyInput(p.valor_previsto), 0)
  const hoje = new Date()
  const recebidoMes = filtradas.filter(p => p.status === 'Recebido' && p.data_recebimento && (() => {
    const d = new Date(p.data_recebimento.split('T')[0] + 'T00:00:00')
    return d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear()
  })()).reduce((s, p) => s + parseMoneyInput(p.valor_recebido), 0)

  return (
    <>
      <div className="topbar">
        <h2>Comissões</h2>
        <button className="btn btn-primary" onClick={() => setModalImportar(true)}>+ Importar Extrato</button>
      </div>
      <div className="page-content">
        <div className="metrics" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 20 }}>
          <div className="metric-card">
            <div className="metric-label">A Receber</div>
            <div className="metric-value" style={{ color: 'var(--primary)' }}>R$ {aReceber.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Recebido do mês</div>
            <div className="metric-value val-pos">R$ {recebidoMes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Atrasado</div>
            <div className="metric-value val-neg">R$ {atrasado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          </div>
        </div>

        <div className="card">
          <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #eee' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 160 }}>
                <SelecaoMultipla label="Status" opcoes={['Pendente', 'Recebido', 'Atrasado'].map(v => ({ valor: v, label: v }))} valores={filtroStatus} onChange={setFiltroStatus} />
              </div>
              <div style={{ minWidth: 200 }}>
                <SelecaoMultipla label="Seguradora" opcoes={seguradoras.map(s => ({ valor: s, label: s }))} valores={filtroSeguradora} onChange={setFiltroSeguradora} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Vencimento de</label>
                <input className="form-input" type="date" value={filtroVencimentoDe} onChange={e => setFiltroVencimentoDe(e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">até</label>
                <input className="form-input" type="date" value={filtroVencimentoAte} onChange={e => setFiltroVencimentoAte(e.target.value)} />
              </div>
              <button type="button" className="btn btn-secondary btn-sm" onClick={limparFiltros}>Limpar filtros</button>
              <div style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>
                {filtradas.length} {filtradas.length === 1 ? 'parcela' : 'parcelas'}
              </div>
            </div>
          </div>

          <div className="table-wrap">
            <table className="table-borda-destaque table-compacta">
              <thead>
                <tr>
                  {colunasComissoes.map(c => (
                    <SortableTh key={c.key} colKey={c.key} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>{c.label}</SortableTh>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginacao.itensPagina.map(p => {
                  const st = statusInfo(statusEfetivo(p))
                  return (
                    <tr key={`${p.apolice_id}-${p.numero}`}>
                      <td style={{ fontWeight: 500 }}>{p.cliente_nome}</td>
                      <td style={{ textAlign: 'center' }}>
                        {/* Abre a apolice por cima da lista; ao fechar volta pra Comissoes no mesmo lugar */}
                        <button type="button" title="Abrir a apólice" onClick={() => abrirApolice(p.apolice_id)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--primary)', textDecoration: 'underline', fontFamily: 'inherit', fontSize: 'inherit' }}>
                          {p.numero_apolice || p.emissao_numero_apolice || 'Ver apólice'}
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>{p.seguradora || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{p.numero}</td>
                      <td style={{ textAlign: 'center' }}>{fmtData(p.data_vencimento)}</td>
                      <td style={{ textAlign: 'center' }}>{formatMoneyInput(parseMoneyInput(p.valor_previsto))}</td>
                      <td style={{ textAlign: 'center' }}><span className={`badge ${st.cls}`}>{st.label}</span></td>
                      <td style={{ textAlign: 'center' }}>{fmtData(p.data_recebimento)}</td>
                      <td style={{ textAlign: 'center' }}>{p.valor_recebido != null ? formatMoneyInput(parseMoneyInput(p.valor_recebido)) : '—'}</td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button type="button" className="btn btn-secondary btn-sm btn-icon" title="Ver apólice" style={{ color: 'var(--primary)', marginRight: 6 }} onClick={() => abrirApolice(p.apolice_id)}><IconVisualizar size={14} /></button>
                        {p.status === 'Recebido'
                          ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => desfazerBaixa(p)}>Desfazer</button>
                          : <button type="button" className="btn btn-primary btn-sm btn-icon" title="Dar Baixa" onClick={() => setParcelaBaixa(p)}><IconDarBaixaComissao size={14} /></button>}
                      </td>
                    </tr>
                  )
                })}
                {!carregando && filtradas.length === 0 && (
                  <tr><td colSpan={colunasComissoes.length + 1} style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>Nenhuma comissão encontrada</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Paginacao {...paginacao} rotulo="comissão" rotuloPlural="comissões" />
        </div>
      </div>

      {parcelaBaixa && (
        <BaixaComissaoModal parcela={parcelaBaixa} onClose={() => setParcelaBaixa(null)} onSalvo={carregar} />
      )}

      {modalImportar && (
        <ImportarExtratoModal parcelas={parcelas} onClose={() => setModalImportar(false)} onConfirmado={carregar} onVerApolice={abrirApolice} />
      )}

      {documentoApolice && (
        // Apolices (modalOnly) nao e um popup flutuante — e uma pagina inteira que substitui o que
        // estiver na tela (mesmo padrao usado em Propostas.jsx). Como aqui ela abre por cima do
        // modal de importacao (que continua montado, sem perder a lista de itens), precisa do seu
        // proprio overlay fixo com z-index maior, senao fica renderizada atras do fundo do modal.
        <div className="modal-overlay" style={{ zIndex: 300, alignItems: 'flex-start', padding: '20px 20px' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 1100, maxHeight: '95vh', overflowY: 'auto' }}>
            <Apolices
              modalOnly
              hideNavigation
              initialDocument={documentoApolice}
              voltarLabel="Comissões"
              onCloseModal={() => setDocumentoApolice(null)}
              onSaved={async () => { setDocumentoApolice(null); await carregar() }}
            />
          </div>
        </div>
      )}
    </>
  )
}
